import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { createStoreBase } from '@/presentation/stores/resources/storeBase'
import DependencyContainer from '@/presentation/configuration/DependencyContainer'
import { FetchAllAxisUseCase } from '@/core/useCases/referential/FetchAllAxisUseCase'
import { FetchAxisByIdUseCase } from '@/core/useCases/referential/FetchAxisByIdUseCase'
import type { ReferentialAxis } from '@/core/domain/referential/axis/ReferentialAxis'
import { NabooError } from '@/core/infrastructure/services/NabooError'
import type { IReferentialUrlOptions } from '@/core/domain/referential/IReferentialUrlOptions'

export const useReferentialStore = defineStore('referential', () => {
  // Récupération des cas d'utilisation depuis le conteneur d'injection de dépendances
  const fetchAllAxisUseCase = DependencyContainer.resolve<FetchAllAxisUseCase>(FetchAllAxisUseCase)
  const fetchAxisByIdUseCase =
    DependencyContainer.resolve<FetchAxisByIdUseCase>(FetchAxisByIdUseCase)

  // Utilisation de la base commune pour les stores
  const storeBase = createStoreBase(['fetchAllAxes', 'fetchAxisById'])

  // État
  const referential = ref<ReferentialAxis[]>([])

  // Actions
  async function fetchAllAxes(options?: IReferentialUrlOptions) {
    try {
      // Indication du chargement
      storeBase.loading.value.fetchAllAxes = true
      storeBase.resetError()

      // Appel du cas d'utilisation avec les options d'URL
      // La conversion en instance de ReferentialUrlOptions est gérée dans le use case
      const axes = await fetchAllAxisUseCase.execute(options)

      referential.value = axes
      storeBase.updateLastUpdated()

      return axes
    } catch (error) {
      handleError(error, 'Une erreur est survenue lors de la récupération des référentiels.')
      return Promise.reject(error)
    } finally {
      storeBase.loading.value.fetchAllAxes = false
    }
  }

  async function fetchAxisById(id: number, options?: IReferentialUrlOptions) {
    try {
      // Indication du chargement
      storeBase.loading.value.fetchAxisById = true
      storeBase.resetError()

      // Appel du cas d'utilisation avec l'ID et les options d'URL
      // La conversion en instance de ReferentialUrlOptions est gérée dans le use case
      return await fetchAxisByIdUseCase.execute(id, options || {})
    } catch (error) {
      handleError(error, `Une erreur est survenue lors de la récupération de l'axe ${id}.`)
      return Promise.reject(error)
    } finally {
      storeBase.loading.value.fetchAxisById = false
    }
  }

  // Helper pour gérer les erreurs
  function handleError(error: unknown, defaultMessage: string) {
    let nabooError: NabooError

    if (error instanceof NabooError) {
      nabooError = error
    } else if (error instanceof Error) {
      nabooError = new NabooError({
        message: defaultMessage,
        status: 500,
        name: 'ReferentialStoreError',
        code: 'REFERENTIAL_STORE_ERROR',
        originalError: error
      })
    } else {
      nabooError = new NabooError({
        message: defaultMessage,
        status: 500,
        name: 'ReferentialStoreError',
        code: 'REFERENTIAL_STORE_ERROR'
      })
    }

    storeBase.setError(nabooError)
  }

  // Getters
  const getAxisTitleFromDomainId = computed(() => (domainId: number): string => {
    const axis = referential.value.find((axis) =>
      axis.domains?.some((domain) => domain.id === domainId)
    )

    return axis ? axis.title : 'Aucun axe ne correspond à ce domaine'
  })

  const getAxisFromDomainId = computed(() => (domainId: number): ReferentialAxis | null => {
    return (
      referential.value.find((axis) => axis.domains?.some((domain) => domain.id === domainId)) ||
      null
    )
  })

  // Get domain by ID
  function getDomainById(domainId: number) {
    for (const axis of referential.value) {
      if (axis.domains) {
        const domain = axis.domains.find((domain) => domain.id === domainId)
        if (domain) return domain
      }
    }
    return null
  }

  // Get task by ID
  function getTaskById(taskId: number) {
    for (const axis of referential.value) {
      if (axis.domains) {
        for (const domain of axis.domains) {
          if (domain.tasks) {
            const task = domain.tasks.find((task) => task.id === taskId)
            if (task) return task
          }
        }
      }
    }
    return null
  }

  // Reset du store
  function reset() {
    referential.value = []
    storeBase.resetBase()
  }

  return {
    // État
    referential,
    ...storeBase,

    // Actions
    fetchAllAxes,
    fetchAxisById,
    reset,
    getDomainById,
    getTaskById,

    // Getters
    getAxisTitleFromDomainId,
    getAxisFromDomainId
  }
})
